/**
 * Payment tracking (D-29).
 *
 * Approved claims that have not been paid, and the ones that have. The reference is what makes
 * "where is my reimbursement?" answerable without an email to HR — managers see the same fact on
 * their own claims, which is the point of recording it here rather than in a spreadsheet.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import { ClaimStatusBadge, Money, PageHeader, RefLink } from '@/components/bits';
import { Card, EmptyState, Table, Td, Th } from '@/components/ui';
import { activityLabel } from '@/lib/policy';
import { daysBetween, formatDate, sgtToday } from '@/lib/dates';

export function HrPayments() {
  const session = useSession();
  const unpaid = useQuery({
    queryKey: ['claims', 'unpaid', session.year],
    queryFn: () => api.claims({ budget_year: session.year, status: ['approved'], limit: 200 }),
  });
  const paid = useQuery({
    queryKey: ['claims', 'paid', session.year],
    queryFn: () => api.claims({ budget_year: session.year, status: ['paid'], limit: 50 }),
  });

  const today = sgtToday();
  const owing = [...(unpaid.data?.items ?? [])].sort((a, b) =>
    (a.submitted_at ?? a.updated_at).localeCompare(b.submitted_at ?? b.updated_at),
  );
  const total = owing.reduce((sum, item) => sum + (item.reimbursable_cents ?? 0), 0);

  return (
    <>
      <PageHeader title="Payments" subtitle="Approved and settled, waiting on the money to move." />

      <Card
        title={`${owing.length} awaiting payment`}
        action={
          owing.length > 0 ? (
            <span className="text-sm">
              <span className="text-muted-foreground">Total </span>
              <Money cents={total} />
            </span>
          ) : null
        }
        className="mb-6"
      >
        {unpaid.isPending ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : owing.length === 0 ? (
          <EmptyState title="Nothing outstanding">
            Every approved claim has been marked paid.
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Event</Th>
                <Th>Department</Th>
                <Th numeric>Reimbursable</Th>
                <Th>Approved</Th>
                <Th>Stage</Th>
              </tr>
            </thead>
            <tbody>
              {owing.map((item) => {
                const days = daysBetween(item.updated_at.slice(0, 10), today);
                return (
                  <tr key={item.id}>
                    <Td>
                      <RefLink to={`/claims/${item.id}`} refNo={item.ref_no} />
                    </Td>
                    <Td>
                      {activityLabel(item.activity_type)}
                      <p className="text-muted-foreground text-xs">
                        {formatDate(item.actual_date)}
                      </p>
                    </Td>
                    <Td className="text-xs">{item.department_name}</Td>
                    <Td numeric>
                      <Money cents={item.reimbursable_cents} />
                    </Td>
                    <Td>
                      <span
                        className={days >= 14 ? 'text-[var(--color-warning)] text-sm' : 'text-sm'}
                      >
                        {days === 0 ? 'today' : `${days} ${days === 1 ? 'day' : 'days'} ago`}
                      </span>
                    </Td>
                    <Td>
                      <ClaimStatusBadge status={item.status} />
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <Card title="Recently paid">
        {(paid.data?.items ?? []).length === 0 ? (
          <p className="text-muted-foreground text-sm">Nothing has been paid yet.</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Department</Th>
                <Th numeric>Paid</Th>
                <Th>On</Th>
              </tr>
            </thead>
            <tbody>
              {(paid.data?.items ?? []).map((item) => (
                <tr key={item.id}>
                  <Td>
                    <RefLink to={`/claims/${item.id}`} refNo={item.ref_no} />
                  </Td>
                  <Td className="text-xs">{item.department_name}</Td>
                  <Td numeric>
                    <Money cents={item.reimbursable_cents} />
                  </Td>
                  <Td className="whitespace-nowrap">{formatDate(item.paid_at)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
